
import React from 'react';

export default function EditTermModal({ showEditModal, closeEditModal, term, triggerRefresh, openMediaLibrary }) {
  const [editForm, setEditForm] = React.useState(null);
  const [editSubmitting, setEditSubmitting] = React.useState(false);
  const [editSuccess, setEditSuccess] = React.useState(null);

  React.useEffect(() => {
    if (showEditModal && term) {
      setEditForm({
        term_id: term.term_id || 0,
        taxonomy: term.taxonomy || '',
        name: term.name || '',
        slug: term.slug || '',
        description: term.description || '',
        color: term?.color ? term?.color : '#0000ff',
        image: term?.image ? term.image : '',
        type: term.type || 'select',
        select: term?.select ? term.select : '',
      });
      setEditSuccess(null);
    }
  }, [showEditModal, term]);

  const handleEditField = (k, v) => setEditForm((s) => ({ ...s, [k]: v }));

  const submitUpdateTerm = async (e) => {
    e && e.preventDefault();
    setEditSubmitting(true);
    setEditSuccess(null);
    try {
      const form = new FormData();
      form.append('action', 'tpwvs_update_term');
      form.append('security', window.tpwvs_admin_settings.update_term_nonce || '');
      form.append('term_id', editForm.term_id);
      form.append('taxonomy', editForm.taxonomy);
      form.append('name', editForm.name);
      form.append('slug', editForm.slug);
      form.append('description', editForm.description);
      if (editForm.color) form.append('color', editForm.color);
      if (editForm.image) form.append('image', editForm.image);
      if (editForm.select) form.append('select', editForm.select);

      const res = await fetch(window.tpwvs_admin_settings.api_url, { method: 'POST', body: form });
      const json = await res.json();
      if (json && json.success) {
        setEditSuccess({ message: json.data && json.data.message ? json.data.message : 'Term updated' });
        if (triggerRefresh) triggerRefresh();
      } else {
        setEditSuccess({ error: json.data && json.data.message ? json.data.message : 'Update failed' });
      }
    } catch (err) {
      setEditSuccess({ error: err.message || 'Network error' });
    } finally {
      setEditSubmitting(false);
    }
  };

  if (!showEditModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black opacity-40" onClick={closeEditModal} />
      <div className="bg-white rounded-lg shadow-lg z-50 w-full max-w-lg p-6">
        <h4 className="text-lg font-semibold mb-2">Edit Term</h4>
        <p className="text-sm text-gray-700 mb-4">Update the term details.</p>
        {editSuccess && editSuccess.message ? (
          <div className="mb-4">
            <div className="text-green-700">{editSuccess.message}</div>
            <div className="mt-3"><button onClick={closeEditModal} className="px-3 py-1 bg-indigo-600 text-white rounded">Close</button></div>
          </div>
        ) : (
          editForm && (
            <form onSubmit={submitUpdateTerm}>
              <div className="grid grid-cols-1 gap-3">
                <label className="block">
                  <div className="text-sm font-semibold text-heading mb-1">Name</div>
                  <input required value={editForm.name} onChange={(e) => handleEditField('name', e.target.value)} className="block w-full rounded-md border border-gray2 bg-white px-2 py-1 text-base text-gray-900 focus:outline-none focus:border-theme" />
                </label>
                <label className="block">
                  <div className="text-sm font-semibold text-heading mb-1">Slug</div>
                  <input value={editForm.slug} onChange={(e) => handleEditField('slug', e.target.value)} className="block w-full rounded-md border border-gray2 bg-white px-2 py-1 text-base text-gray-900 focus:outline-none focus:border-theme" />
                </label>
                <label className="block">
                  <div className="text-sm font-semibold text-heading mb-1">Description</div>
                  <textarea value={editForm.description} onChange={(e) => handleEditField('description', e.target.value)} className="block w-full rounded-md border border-gray2 bg-white px-2 py-1 text-base text-gray-900 focus:outline-none focus:border-theme" rows={3} />
                </label>

                {editForm.type === 'color' && (
                  <label className="block">
                    <div className="text-sm font-semibold text-heading mb-1">Color</div>
                    <input type="color" value={editForm.color} onChange={(e) => handleEditField('color', e.target.value)} className="inline-block w-10 h-8 p-0 border border-gray2 rounded focus:outline-none" style={{ width: 40, height: 28, padding: 0 }} />
                    <p className="text-sm text-gray-600 !m-0">Pick a color for this term.</p>
                  </label>
                )}

                {editForm.type === 'image' && (
                  <label className="block">
                    <div className="text-sm font-semibold text-heading mb-1">Image</div>
                    <div className="flex items-center gap-3">
                      <input readOnly value={editForm.image} placeholder="Image URL" className="block w-full rounded-md border border-gray2 bg-white px-2 py-1 text-sm text-gray-700 focus:outline-none focus:border-theme" />
                      <button type="button" onClick={() => openMediaLibrary((url) => handleEditField('image', url))} className="px-2 py-1 bg-gray-100 border rounded text-sm">Choose</button>
                    </div>
                    {editForm.image && (
                      <div className="mt-2">
                        <img
                          src={(() => {
                            try {
                              return encodeURI(editForm.image);
                            } catch (e) {
                              return editForm.image;
                            }
                          })()}
                          alt="preview"
                          className="w-20 h-20 object-cover rounded"
                        />
                      </div>
                    )}
                    <p className="text-sm text-gray-600 !m-0">Upload or choose an image to display for this term.</p>
                  </label>
                )}

                {editForm.type === 'select' && (
                  <label className="block">
                    <div className="text-sm font-semibold text-heading mb-1">Tooltip Label</div>
                    <input value={editForm.select} onChange={(e) => handleEditField('select', e.target.value)} className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-base text-gray-900" />
                  </label>
                )}

                {editSuccess && editSuccess.error && (
                  <div role="alert" className="mt-2 p-2 bg-red-50 border border-red-200 text-sm text-red-800 rounded-md">{editSuccess.error}</div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button type="submit" disabled={editSubmitting} className="px-3 py-1.5 bg-theme text-white rounded text-sm">{editSubmitting ? 'Saving…' : 'Save'}</button>
                <button type="button" onClick={closeEditModal} className="px-2 py-1 border rounded text-sm">Cancel</button>
              </div>
            </form>
          )
        )}
      </div>
    </div>
  );
}
